"use client";

import React, { useEffect, useRef, useState } from "react";
import "@xterm/xterm/css/xterm.css";

type TerminalInstance = {
  clear: () => void;
  dispose: () => void;
  focus: () => void;
  open: (element: HTMLElement) => void;
  onData: (callback: (data: string) => void) => { dispose: () => void };
  write: (data: string) => void;
};

type Session = {
  id: string;
  taskId: string;
  tmuxSession?: string | null;
  cwd?: string | null;
  status: string;
  createdAt: string;
  closedAt?: string | null;
  _count?: {
    commands: number;
    outputs: number;
  };
};

type TerminalOutput = {
  id: number;
  content: string;
};

const quickActions = [
  { label: "Run Codex", input: "codex\r", tone: "primary" as const },
  { label: "Ctrl+C", input: "\u0003" },
  { label: "Clear", input: "clear\r" },
  { label: "pwd", input: "pwd\r" },
  { label: "ls -la", input: "ls -la\r" },
  { label: "git status", input: "git status -sb\r" },
  { label: "npm install", input: "npm install\r" },
  { label: "npm test", input: "npm test\r" },
  { label: "npm run dev", input: "npm run dev\r" },
];

const sectionStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #dbe4f0",
  borderRadius: 18,
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
};

const secondaryButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  borderRadius: 12,
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 600,
};

export default function TerminalPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<TerminalInstance | null>(null);
  const currentSessionRef = useRef<string>("");
  const lastOutputIdRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputBufferRef = useRef("");
  const replayModeRef = useRef(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState("");
  const [cwd, setCwd] = useState("/home/hpn/dev/h");
  const [command, setCommand] = useState("codex");
  const [statusText, setStatusText] = useState("Idle");
  const [creating, setCreating] = useState(false);
  const [lastOutputId, setLastOutputId] = useState(0);

  currentSessionRef.current = sessionId;
  lastOutputIdRef.current = lastOutputId;

  const activeSession = sessions.find((item) => item.id === sessionId) || null;

  async function sendInput(input: string) {
    if (!currentSessionRef.current || replayModeRef.current) {
      return;
    }

    await fetch(`/api/terminal-sessions/${currentSessionRef.current}/input`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ input }),
    });
  }

  function scheduleFlush() {
    if (flushTimerRef.current) {
      return;
    }

    flushTimerRef.current = setTimeout(async () => {
      flushTimerRef.current = null;
      const next = inputBufferRef.current;
      inputBufferRef.current = "";
      if (next) {
        await sendInput(next);
      }
    }, 80);
  }

  async function loadSessions(preferredSessionId?: string) {
    const response = await fetch("/api/terminal-sessions", { cache: "no-store" });
    const data = await response.json();
    const nextSessions = (data.sessions || []) as Session[];
    setSessions(nextSessions);

    if (preferredSessionId) {
      const found = nextSessions.find((item) => item.id === preferredSessionId);
      if (found) {
        setSessionId(found.id);
        return;
      }
    }

    if (!currentSessionRef.current && nextSessions[0]) {
      setSessionId(nextSessions[0].id);
    }
  }

  async function loadOutputs(reset: boolean) {
    if (!currentSessionRef.current || !termRef.current) {
      return;
    }

    const afterId = reset ? 0 : lastOutputIdRef.current;
    const response = await fetch(
      `/api/terminal-outputs?sessionId=${encodeURIComponent(currentSessionRef.current)}&afterId=${afterId}&take=${reset ? 500 : 200}`,
      { cache: "no-store" },
    );
    const data = await response.json();
    const outputs = (data.outputs || []) as TerminalOutput[];

    if (reset) {
      termRef.current.clear();
    }

    for (const output of outputs) {
      termRef.current.write(output.content);
    }

    if (outputs.length > 0) {
      setLastOutputId(outputs[outputs.length - 1].id);
    } else if (reset) {
      setLastOutputId(0);
    }
  }

  async function createNewSession() {
    try {
      setCreating(true);
      setStatusText("Creating terminal session...");
      const response = await fetch("/api/terminal-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cwd }),
      });
      const data = await response.json();
      const created = data.session as Session | undefined;
      if (created?.id) {
        await loadSessions(created.id);
        setStatusText(`Session ${created.tmuxSession || created.id} is ready.`);
      } else {
        setStatusText("Failed to create session.");
      }
    } finally {
      setCreating(false);
    }
  }

  async function closeSession(targetSessionId: string) {
    const response = await fetch(`/api/terminal-sessions/${targetSessionId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      setStatusText("Unable to close the session.");
      return;
    }

    const nextSessions = sessions.filter((item) => item.id !== targetSessionId);
    setSessions(nextSessions);
    if (sessionId === targetSessionId) {
      setSessionId(nextSessions[0]?.id || "");
      setLastOutputId(0);
    }
    setStatusText("Session closed.");
  }

  async function runCommand(nextCommand?: string) {
    const raw = (nextCommand ?? command).trim();
    if (!raw || !currentSessionRef.current) {
      return;
    }

    setStatusText(`Running: ${raw}`);
    await sendInput(`${raw}\r`);
  }

  useEffect(() => {
    let mounted = true;

    async function initTerminal() {
      if (!containerRef.current) {
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const requestedSessionId = params.get("session") || undefined;
      replayModeRef.current = params.get("replay") === "1";

      const { Terminal } = await import("@xterm/xterm");
      const terminal = new Terminal({
        cursorBlink: !replayModeRef.current,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 14,
        rows: 26,
        theme: {
          background: "#020617",
          foreground: "#dbeafe",
          cursor: "#38bdf8",
          black: "#0f172a",
          green: "#34d399",
          blue: "#60a5fa",
          yellow: "#fbbf24",
          red: "#f87171",
        },
      }) as TerminalInstance;

      terminal.open(containerRef.current);
      terminal.focus();
      termRef.current = terminal;
      terminal.write("Codex terminal workspace is loading...\r\n");

      if (!replayModeRef.current) {
        terminal.onData((data) => {
          inputBufferRef.current += data;
          scheduleFlush();
        });
      }

      await loadSessions(requestedSessionId);

      if (mounted && !requestedSessionId) {
        const response = await fetch("/api/terminal-sessions", { cache: "no-store" });
        const data = await response.json();
        if ((data.sessions || []).length === 0) {
          await createNewSession();
        }
      }
    }

    initTerminal();

    return () => {
      mounted = false;
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }
      termRef.current?.dispose();
      termRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    setStatusText(replayModeRef.current ? "Replay mode" : "Attached");
    setLastOutputId(0);
    void loadOutputs(true);
    termRef.current?.focus();

    if (replayModeRef.current) {
      return;
    }

    const interval = setInterval(() => {
      void loadOutputs(false);
    }, 900);

    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <section
        style={{
          ...sectionStyle,
          padding: 24,
          background:
            "linear-gradient(135deg, #082f49 0%, #0f172a 58%, #172554 100%)",
          color: "#eff6ff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div style={{ maxWidth: 720 }}>
            <div
              style={{
                display: "inline-flex",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(56, 189, 248, 0.15)",
                border: "1px solid rgba(125, 211, 252, 0.35)",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Codex Terminal Workspace
            </div>
            <h1 style={{ margin: "14px 0 10px", fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}>
              Chạy Codex và thao tác hệ thống ngay trong dashboard
            </h1>
            <p style={{ margin: 0, color: "#bfdbfe", lineHeight: 1.6 }}>
              Màn hình này giữ session tmux riêng, stream output về dashboard, và bổ sung quick toolbar để thay phần lớn thao tác SSH thường dùng.
            </p>
          </div>

          <div
            style={{
              minWidth: 280,
              background: "rgba(15, 23, 42, 0.35)",
              border: "1px solid rgba(148, 163, 184, 0.25)",
              borderRadius: 16,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 12, color: "#93c5fd", marginBottom: 6 }}>Session status</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{statusText}</div>
            <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.6 }}>
              Session: {activeSession?.tmuxSession || activeSession?.id || "none"}
              <br />
              CWD: {activeSession?.cwd || cwd}
            </div>
          </div>
        </div>
      </section>

      <section style={{ ...sectionStyle, padding: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>Quick Toolbar</h2>
            <p style={{ margin: "6px 0 0", color: "#475569" }}>
              Một chạm để mở Codex, kiểm tra repo, dọn màn hình, hoặc gửi phím điều khiển.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              value={command}
              onChange={(event) => setCommand(event.target.value)}
              placeholder="Custom command"
              style={{
                minWidth: 260,
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 14,
              }}
            />
            <button
              onClick={() => void runCommand()}
              disabled={!sessionId}
              style={{
                border: "none",
                background: "#0f766e",
                color: "#ffffff",
                borderRadius: 12,
                padding: "10px 16px",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Run command
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => void sendInput(action.input)}
              disabled={!sessionId || replayModeRef.current}
              style={{
                border: action.tone === "primary" ? "none" : "1px solid #cbd5e1",
                background: action.tone === "primary" ? "#2563eb" : "#ffffff",
                color: action.tone === "primary" ? "#ffffff" : "#0f172a",
                borderRadius: 999,
                padding: "10px 14px",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 20,
          alignItems: "start",
        }}
      >
        <section style={{ ...sectionStyle, padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>Sessions</h2>
            <p style={{ margin: "6px 0 0", color: "#475569" }}>
              Tạo nhiều terminal và chuyển giữa các workspace đang mở.
            </p>
          </div>

          <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
                Working directory
              </span>
              <input
                value={cwd}
                onChange={(event) => setCwd(event.target.value)}
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 12,
                  padding: "10px 12px",
                }}
              />
            </label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={() => void createNewSession()}
                disabled={creating}
                style={{
                  border: "none",
                  background: "#0f172a",
                  color: "#ffffff",
                  borderRadius: 12,
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {creating ? "Creating..." : "New session"}
              </button>
              <button
                onClick={() => void loadSessions()}
                style={secondaryButtonStyle}
              >
                Refresh
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {sessions.map((item) => {
              const selected = item.id === sessionId;
              return (
                <div
                  key={item.id}
                  style={{
                    border: selected ? "1px solid #2563eb" : "1px solid #dbe4f0",
                    background: selected ? "#eff6ff" : "#f8fafc",
                    borderRadius: 16,
                    padding: 14,
                  }}
                >
                  <button
                    onClick={() => setSessionId(item.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                      {item.tmuxSession || item.id}
                    </div>
                    <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                      {item.cwd || "No cwd"}
                      <br />
                      Commands: {item._count?.commands || 0} | Outputs: {item._count?.outputs || 0}
                    </div>
                  </button>
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      onClick={() => setSessionId(item.id)}
                      style={secondaryButtonStyle}
                    >
                      Attach
                    </button>
                    <button
                      onClick={() => void closeSession(item.id)}
                      style={{
                        ...secondaryButtonStyle,
                        color: "#b91c1c",
                        borderColor: "#fecaca",
                        background: "#fff1f2",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              );
            })}

            {sessions.length === 0 && (
              <div
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 16,
                  padding: 18,
                  color: "#64748b",
                  background: "#f8fafc",
                }}
              >
                Chưa có terminal session nào. Tạo một session mới để bắt đầu chạy Codex.
              </div>
            )}
          </div>
        </section>

        <section style={{ ...sectionStyle, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              padding: "18px 20px",
              borderBottom: "1px solid #dbe4f0",
              background: "#f8fafc",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>Live terminal</h2>
              <p style={{ margin: "6px 0 0", color: "#475569" }}>
                Gõ trực tiếp trong terminal hoặc dùng toolbar để đẩy lệnh nhanh.
              </p>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 999,
                background: "#dbeafe",
                color: "#1d4ed8",
                fontWeight: 700,
              }}
            >
              {replayModeRef.current ? "Replay" : activeSession?.status || "Detached"}
            </div>
          </div>

          <div
            style={{
              background:
                "radial-gradient(circle at top left, rgba(14, 165, 233, 0.15), transparent 30%), #020617",
              minHeight: 620,
              padding: 16,
            }}
          >
            <div
              ref={containerRef}
              style={{
                minHeight: 588,
                width: "100%",
                borderRadius: 16,
                overflow: "hidden",
              }}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
